/**
 * LanguageContext — única fuente de verdad del idioma (reescrito oct-2026, arreglo de raíz).
 *
 * Orden de decisión del idioma activo:
 *   1. Preferencia guardada en la cuenta (users.language) cuando hay sesión — la fija Ajustes → Idioma.
 *   2. Elección explícita local (localStorage["lineage_lang"]).
 *   3. Idioma del dispositivo/navegador (navigator.language).
 *   4. Inglés.
 *
 * Dos formas de traducir, un solo diccionario:
 *   t('auth.signIn')              → clave con puntos (locales/*.json: { en, es, pt, fr })
 *   t('Pagar ahora')              → frase literal en español (locales/phrases.json: { en, pt, fr })
 *   t('Hola {0}', nombre)         → interpolación posicional · t('Hola {name}', { name })
 *   tr(...)                       → misma función fuera de React (menús, catálogos, utils);
 *                                   el árbol se vuelve a montar al cambiar idioma, así que siempre está al día.
 *
 * Si falta una traducción: idioma del usuario → inglés → texto fuente. Nunca «cae» a español salvo que
 * el texto fuente sea español y no exista traducción (eso lo detecta scripts/qa/i18n_audit.py).
 */
import { Fragment, createContext, createElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api, { getToken } from '@/lib/api';

export const SUPPORTED_LANGS = ['en', 'es', 'pt', 'fr'];
const STORAGE_KEY = 'lineage_lang';
const DEFAULT_LANG = 'en';
const SOURCE_LANG = 'es';
// es-US: español con formato de EE. UU. ($12.50, punto decimal, reloj de 12 h) — la app opera en USD.
const LOCALE_TAGS = { en: 'en-US', es: 'es-US', pt: 'pt-BR', fr: 'fr-FR' };

export const normalizeLang = (raw) => {
  if (!raw) return null;
  const code = String(raw).toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LANGS.includes(code) ? code : null;
};

export const detectDeviceLang = () => {
  try {
    const list = (typeof navigator !== 'undefined' && Array.isArray(navigator.languages) && navigator.languages.length)
      ? navigator.languages : [typeof navigator !== 'undefined' ? (navigator.language || navigator.userLanguage) : null];
    for (const cand of list) { const n = normalizeLang(cand); if (n) return n; }
  } catch { /* noop */ }
  return DEFAULT_LANG;
};

const readStored = () => { try { return normalizeLang(localStorage.getItem(STORAGE_KEY)); } catch { return null; } };
const detectInitialLang = () => readStored() || detectDeviceLang();

let currentLang = detectInitialLang();

// Frases por idioma (rendimiento C): se descargan solo para el idioma activo; el español es el idioma fuente.
const phrasesByLang = { es: {} };
const catalogByLang = {};   // claves punteadas (common.save…) por idioma, generadas por scripts/split-phrases.js
const phraseLoads = {};
export const loadPhrases = (lang) => {
  const norm = normalizeLang(lang) || DEFAULT_LANG;
  if (phrasesByLang[norm] && catalogByLang[norm]) return Promise.resolve();
  if (!phraseLoads[norm]) {
    const phr = phrasesByLang[norm] ? Promise.resolve() : import(/* webpackChunkName: "phrases-[request]" */ `@/locales/phrases/${norm}.json`)
      .then((m) => { phrasesByLang[norm] = m.default || m; }).catch(() => { phrasesByLang[norm] = {}; });
    const cat = import(/* webpackChunkName: "catalog-[request]" */ `@/locales/catalog/${norm}.json`)
      .then((m) => { catalogByLang[norm] = m.default || m; }).catch(() => { catalogByLang[norm] = {}; });
    phraseLoads[norm] = Promise.all([phr, cat]).then(() => undefined);
  }
  return phraseLoads[norm];
};
export const getCurrentLang = () => currentLang;
export const localeTag = (lang = currentLang) => LOCALE_TAGS[lang] || LOCALE_TAGS[DEFAULT_LANG];

const isElement = (v) => Boolean(v) && typeof v === 'object' && Boolean(v.$$typeof);
// Como en JSX: false/null/undefined no pintan nada.
const str = (v) => (v === false || v === null || v === undefined ? '' : String(v));

const interpolate = (phrase, args) => {
  if (!args.length || typeof phrase !== 'string') return phrase;
  let out = phrase;
  const first = args[0];
  if (args.length === 1 && first && typeof first === 'object' && !Array.isArray(first) && !isElement(first)) {
    Object.keys(first).forEach((k) => { out = out.replaceAll(`{${k}}`, str(first[k])); });
    return out;
  }
  const list = args.length === 1 && Array.isArray(first) ? first : args;
  if (list.some(isElement)) {
    // Argumentos JSX (iconos, badges): devolvemos nodos intercalados en vez de "[object Object]".
    return phrase.split(/(\{\d+\})/).map((part, idx) => {
      const m = /^\{(\d+)\}$/.exec(part);
      if (!m) return part;
      const v = list[Number(m[1])];
      return isElement(v) ? createElement(Fragment, { key: `i${idx}` }, v) : str(v);
    });
  }
  list.forEach((v, i) => { out = out.replaceAll(`{${i}}`, str(v)); });
  return out;
};

const lookupDotted = (key, lang) => {
  const parts = key.split('.');
  let node = catalogByLang[lang] || catalogByLang[DEFAULT_LANG];
  for (const p of parts) {
    if (node && typeof node === 'object' && p in node) node = node[p];
    else return undefined;
  }
  return typeof node === 'string' ? node : undefined;
};

export const translate = (lang, key, ...args) => {
  if (!key || typeof key !== 'string') return '';
  let phrase = key.includes('.') && !key.includes(' ') ? lookupDotted(key, lang) : undefined;
  if (phrase === undefined) {
    if (lang === SOURCE_LANG) phrase = key;
    else {
      phrase = (phrasesByLang[lang] || {})[key] || key;
    }
  }
  return interpolate(phrase, args);
};

/** Traductor fuera de React (catálogos, menús, utils). Usa el idioma activo. */
export const tr = (key, ...args) => translate(currentLang, key, ...args);

const LanguageContext = createContext({ lang: currentLang, setLang: () => {}, t: tr, languages: SUPPORTED_LANGS, localeTag });

const persistRemote = (patch) => {
  try {
    if (!getToken()) return Promise.resolve();  // visitante sin sesión: solo local (un 401 aquí redirigía a /auth)
    return api.patch('/users/me', patch).catch(() => {});
  } catch { return Promise.resolve(); }
};

/** Los catálogos de módulo (menús, tipos de notificación…) se evalúan una vez: recargar garantiza que todo cambie. */
const reloadSoon = (ms = 350) => { try { setTimeout(() => window.location.reload(), ms); } catch { /* noop */ } };

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(currentLang);

  const applyLang = useCallback((next, { remote = false, reload = false } = {}) => {
    const norm = normalizeLang(next) || DEFAULT_LANG;
    if (!phrasesByLang[norm] || !catalogByLang[norm]) { loadPhrases(norm).then(() => applyLang(next, { remote, reload })); return; }
    currentLang = norm;
    setLangState(norm);
    try { localStorage.setItem(STORAGE_KEY, norm); } catch { /* noop */ }
    try { window.dispatchEvent(new CustomEvent('lineage_lang_changed', { detail: { lang: norm } })); } catch { /* noop */ }
    const saved = remote ? persistRemote({ language: norm }) : Promise.resolve();
    if (reload) Promise.resolve(saved).finally(() => reloadSoon());
  }, []);

  // Elección explícita del usuario (Ajustes → Idioma): local + cuenta + recarga para que todo cambie.
  const setLang = useCallback((next) => applyLang(next, { remote: true, reload: true }), [applyLang]);

  useEffect(() => {
    try { document.documentElement.setAttribute('lang', lang); } catch { /* noop */ }
  }, [lang]);

  const t = useCallback((key, ...args) => translate(lang, key, ...args), [lang]);
  const value = useMemo(() => ({ lang, setLang, applyLang, t, languages: SUPPORTED_LANGS, localeTag: () => localeTag(lang) }),
    [lang, setLang, applyLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

/** Vuelve a montar la UI al cambiar idioma para que `tr()` fuera de React también se refresque. */
export const LanguageRemount = ({ children }) => {
  const { lang } = useContext(LanguageContext);
  return <div key={lang} className="contents">{children}</div>;
};

/**
 * Sincroniza con la cuenta (dentro de AuthProvider):
 *   · la cuenta tiene idioma elegido en Ajustes (users.language) → manda sobre este dispositivo (una recarga, sin bucle);
 *   · si no, NO se guarda como elección: solo se anota el idioma del dispositivo (users.device_language) para
 *     que correos y avisos fuera de una petición salgan en su idioma.
 */
export const LanguageAccountSync = ({ user }) => {
  const { lang, applyLang } = useContext(LanguageContext);
  const userId = user?.id || null;
  const saved = normalizeLang(user?.language);
  const device = normalizeLang(user?.device_language);
  useEffect(() => {
    if (!userId) return;
    if (saved) {
      if (saved !== lang && readStored() !== saved) applyLang(saved, { reload: true });
    } else if (device !== lang) {
      persistRemote({ device_language: lang });
    }
  }, [userId, saved, device]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

export const useLang = () => useContext(LanguageContext);
export default LanguageContext;
